<template>
  <v-row class="my-3">
    <v-col
      v-for="({ icon, data, chartData, hasChart }, index) in cardsData"
      :key="index"
      :lg="isOwner ? 3 : 4"
      :sm="isOwner ? 6 : 4"
    >
      <div class="icon-box">
        <percentage-box :box-data="data">
          <template #icon>
            <component :is="icon" />
          </template>

          <template
            v-if="hasChart"
            #content
          >
            <div class="chart-parent">
              <pie-chart :series="chartData" />
            </div>
          </template>
        </percentage-box>
      </div>
    </v-col>

    <v-col
      v-if="isOwner"
      lg="3"
      sm="6"
    >
      <div class="icon-box">
        <cloud-lines-balance-card />
      </div>
    </v-col>
  </v-row>
</template>

<script>
  import { useUserStore } from '@bv/stores/user';
  import { mapState } from 'pinia';

  import CloudLinesBalanceCard from '@/components/Dashboard/CloudLinesBalanceCard.vue';
  import { TotalCallsIcon, OutboundCallsIcon, InboundCallsIcon } from '@/components/Icons/dashboard';
  import PercentageBox from '@/components/SharedComponents/Boxes/PercentageBox.vue';
  import PieChart from '@/components/SharedComponents/Charts/PieChart.vue';
  import { useDashboardStore } from '@/stores/reports/dashboard.store';
  import { formatPercentage } from '@/utilities';


  export default {
    components: {
      InboundCallsIcon,
      OutboundCallsIcon,
      TotalCallsIcon,
      PieChart,
      PercentageBox,
      CloudLinesBalanceCard,
    },
    computed: {
      ...mapState(useDashboardStore, ['summaryTotals', 'inboundCalls', 'outboundCalls']),
      ...mapState(useUserStore, ['currentWorkspace']),
      isOwner() {
        return !!this.currentWorkspace?.is_owner;
      },
      cardsData() {
        return [
          {
            data: {
              title: this.$t('dashboard.total_calls'),
              value: this.summaryTotals.total || 0,
              percentage: null,
              color: 'neutral100',
            },
            icon: TotalCallsIcon,
            hasChart: true,
            chartData: this.chartData,
          },
          {
            data: {
              title: this.$t('dashboard.inbound_calls'),
              value: this.inboundCalls,
              color: 'blue50',
              percentage: this.inboundPercent,
            },
            icon: InboundCallsIcon,
          },
          {
            data: {
              title: this.$t('dashboard.outbound_calls'),
              value: this.outboundCalls,
              color: 'green50',
              percentage: this.outboundPercent,
            },
            icon: OutboundCallsIcon,
          },
        ];
      },
      inboundPercent() {
        return formatPercentage(this.inboundCalls, {
          decimalPlaces: 1,
          baseValue: this.summaryTotals.total,
          withoutPercent: true,
        });
      },
      outboundPercent() {
        return formatPercentage(this.outboundCalls, {
          decimalPlaces: 1,
          baseValue: this.summaryTotals.total,
          withoutPercent: true,
        });
      },
      chartData() {
        // read CSS vars
        const rootStyles = getComputedStyle(document.documentElement);
        return {
          labels: [this.$t('dashboard.inbound'), this.$t('dashboard.outbound')],
          datasets: [
            {
              backgroundColor: [rootStyles.getPropertyValue('--color-base-500').trim(), '#43B284'],
              data: [this.inboundPercent, this.outboundPercent],
              borderWidth: 1,
            },
          ],
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .icon-box {
    height: 100%;
    border-radius: 16px;
    border: 1px solid var(--gray100);
    background-color: var(--shadesWhite);
    padding: 16px;

    @include responsive(max, 991px) {
      padding: 8px;
    }

    @include responsive(max, 768px) {
      svg {
        width: 16px;
        height: 16px;
      }
    }

    .chart-parent {
      margin-inline-start: auto;
      width: 60px;
      height: 60px;

      @include responsive(max, 768px) {
        width: 32px;
        height: 32px;
      }
    }

    .loader {
      height: 64px;
      display: flex;
      align-items: center;
    }
  }
</style>
